import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { useCommunityAuth } from "../../contexts/CommunityAuthContext";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBuilding,
  faCog,
  faClipboardList,
  faCheck,
  faArrowRight,
  faArrowLeft,
  faExclamationTriangle,
  faShieldAlt,
  faBug,
} from "@fortawesome/free-solid-svg-icons";
import { InlineSpinner } from "../../componentes/LoadingIndicator";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import FormField from "./FormField";
import ReCaptcha from "../../componentes/ReCaptcha";
import SuccessNotification from "../../componentes/SuccessNotification";
import {
  organizationValidationSchema,
  personalValidationSchema,
} from "./validationSchemas";
import {
  impactLevels,
  organizationTypes,
} from "./formConfig";
import { sanitizeFormData } from "../../utils/security";
import { handleSubmissionError } from "../../utils/errorHandling";
import { API_BASE_URL } from '../../api/config';

const baseUrl = API_BASE_URL;


const ScrollToError = () => {
  const { submitCount, errors } = useFormikContext();
  const prevSubmitCount = useRef(submitCount);

  useEffect(() => {
    if (
      submitCount > prevSubmitCount.current &&
      Object.keys(errors).length > 0
    ) {
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
    prevSubmitCount.current = submitCount;
  }, [submitCount, errors]);

  return null;
};

const SteppedIncidentForm = () => {
  const { user } = useCommunityAuth();
  const { type } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [formType] = useState(searchParams.get('type') || "organization");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attackTypes, setAttackTypes] = useState([]);
  const [personalSystemFunctions, setPersonalSystemFunctions] = useState([]);
  const [orgSystemFunctions, setOrgSystemFunctions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [recaptchaRef, setRecaptchaRef] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedTrackingId, setSubmittedTrackingId] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [attackRes, personalSysRes, orgSysRes] = await Promise.all([
          axios.get(`${baseUrl}/api/incident-reports/attack-types/`),
          axios.get(`${baseUrl}/api/incident-reports/personal-system-functions/`),
          axios.get(`${baseUrl}/api/incident-reports/organizational-system-functions/`),
        ]);
        const fetchedAttackTypes = attackRes.data.map((item) => ({
          id: item.id,
          name: item.name,
        }));
        const fetchedPersonalSys = personalSysRes.data.map((item) => ({
          id: item.id,
          name: item.name,
        }));
        const fetchedOrgSys = orgSysRes.data.map((item) => ({
          id: item.id,
          name: item.name,
        }));

        setAttackTypes([...fetchedAttackTypes, { id: "other", name: "Other" }]);
        setPersonalSystemFunctions([...fetchedPersonalSys, { id: "other", name: "Other" }]);
        setOrgSystemFunctions([...fetchedOrgSys, { id: "other", name: "Other" }]);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load form options. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const getSteps = () => {
    const baseSteps = [
      {
        id: "personal",
        title: t("incident report.steps.personal_info"),
        icon: faUser,
        fields: ["full_name", "email", "phone_number", "address"]
      }
    ];

    if (formType === "organization") {
      baseSteps.push({
        id: "organization",
        title: t("incident report.steps.organization"),
        icon: faBuilding,
        fields: ["organization_name", "organization_type", "job_title"]
      });
    }

    baseSteps.push(
      {
        id: "technical",
        title: t("incident report.steps.technical_details"),
        icon: faCog,
        fields: ["attack_type", "system_functions", "affected_systems", "impact"]
      },
      {
        id: "incident",
        title: t("incident report.steps.incident_details"),
        icon: faClipboardList,
        fields: ["time", "incident_description", "incident_files"]
      }
    );

    return baseSteps;
  };

  const steps = getSteps();

  const getInitialValues = () => {
    const baseValues = {
      full_name: user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "",
      email: user ? user.email : "",
      phone_number: user?.phone || "",
      address: user?.location || "",
      attack_type: "",
      other_attack_type: "",
      system_functions: [],
      other_system_function: "",
      impact: "",
      time: "",
      affected_systems: "",
      incident_description: "",
      incident_files: [],
      recaptcha: "",
    };

    if (formType === "organization") {
      return {
        ...baseValues,
        job_title: user ? user.title : "",
        organization_name: "",
        organization_type: "",
      };
    }

    return baseValues;
  };

  const handleNext = async (validateForm, values, setTouched) => {
    if (currentStep < steps.length - 1) {
      // Get current step fields
      const currentStepFields = steps[currentStep].fields;

      // Create touched object for current step fields
      const touchedFields = {};
      currentStepFields.forEach(field => {
        touchedFields[field] = true;
      });

      // Set touched for current step fields
      setTouched(touchedFields);

      // Validate the form
      const errors = await validateForm();

      // Check if current step has any validation errors
      const currentStepErrors = currentStepFields.some(field => {
        return errors[field];
      });

      if (!currentStepErrors) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Scroll to first error field
        const firstErrorField = currentStepFields.find(field => errors[field]);
        if (firstErrorField) {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.focus();
          }
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (
    values,
    { resetForm, setErrors, setSubmitting }
  ) => {
    try {
      setIsSubmitting(true);
      if (!values.recaptcha) {
        toast.error(t("incident report.ui.recaptcha_error"));
        setIsSubmitting(false);
        return;
      }

      const { sanitized, issues } = sanitizeFormData(values);

      if (issues.length > 0) {
        toast.error("Security issues detected in form data");
        setErrors(
          issues.reduce(
            (acc, issue) => ({
              ...acc,
              [issue.field]: `Security issue: ${issue.issues.join(", ")}`,
            }),
            {}
          )
        );
        return;
      }

      const formData = new FormData();
      Object.entries(sanitized).forEach(([key, value]) => {
        if (key === "system_functions") {
          const filteredFunctions = value.filter((id) => id !== "other");
          filteredFunctions.forEach((id) => formData.append("system_functions_ids", id));
        } else if (key === "organization_type" && value) {
          const intValue = parseInt(value, 10);
          if (!isNaN(intValue)) {
            formData.append(key, intValue);
          }
        } else if (key === "attack_type") {
          if (value === "other") {
            formData.append("attack_type_id", "");
          } else if (value) {
            formData.append("attack_type_id", value);
          }
        } else if (key === "incident_files" && value) {
          Array.from(value).forEach((file) => formData.append("incident_files", file));
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      // Get CSRF token from cookie
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(csrfToken && { "X-CSRFToken": csrfToken })
        },
        withCredentials: true
      };

      const endpoint =
        formType === "organization"
          ? "/api/incident-reports/organization/create/"
          : "/api/incident-reports/personal/create/";

      const response = await axios.post(
        `${baseUrl}${endpoint}`,
        formData,
        config
      );

      if (response.data.status === "success") {
        // Store email and tracking ID and show success notification
        setSubmittedEmail(values.email);
        setSubmittedTrackingId(response.data.tracking_id || 'Not available');
        setShowSuccess(true);

        // Reset form
        resetForm({ values: { ...getInitialValues(), incident_files: null, recaptcha: "" } });
        const fileInput = document.getElementById("incident_files");
        if (fileInput) fileInput.value = "";
        if (recaptchaRef) recaptchaRef.reset();
        setSubmitting(false);
        setCurrentStep(0);
      }
    } catch (error) {
      handleSubmissionError(error, setErrors, toast, t);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (step, values) => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <InlineSpinner size="lg" className="border-primary border-t-transparent" />
          <span className="ml-3 text-lg">{t("incident report.ui.loading")}</span>
        </div>
      );
    }

    switch (step.id) {
      case "personal":
        return (
          <div className="space-y-6">
            <FormField
              name="full_name"
              label={t("incident report.full_name.label")}
              placeholder={t("incident report.full_name.placeholder")}
            />
            <FormField
              name="email"
              label={formType === "organization" ? t("incident report.email.label") : t("incident report.email.label")}
              type="email"
              placeholder={t("incident report.email.placeholder")}
            />
            <FormField
              name="phone_number"
              label={t("incident report.phone_number.label")}
              placeholder={t("incident report.phone_number.placeholder")}
            />
            <FormField
              name="address"
              label={t("incident report.address.label")}
              placeholder={t("incident report.address.placeholder")}
            />
          </div>
        );
      case "organization":
        return (
          <div className="space-y-6">
            <FormField
              name="organization_name"
              label={t("incident report.organization_name.label")}
              placeholder={t("incident report.organization_name.placeholder")}
              required
            />
            <FormField
              name="organization_type"
              label={t("incident report.organization_type.label")}
              as="select"
              options={organizationTypes}
              required
            />
            <FormField
              name="job_title"
              label={t("incident report.job_title.label")}
              placeholder={t("incident report.job_title.placeholder")}
            />
          </div>
        );
      case "technical":
        return (
          <div className="space-y-6">
            <FormField
              name="attack_type"
              label={t("incident report.attack_type.label")}
              as="select"
              options={attackTypes}
              required
            />
            {values.attack_type === "other" && (
              <FormField
                name="other_attack_type"
                label={t("incident report.other_attack_type.label")}
                placeholder={t("incident report.other_attack_type.placeholder")}
                required
              />
            )}
            <FormField
              name="system_functions"
              label={t("incident report.system_functions.label")}
              type="checkbox"
              options={formType === "organization" ? orgSystemFunctions : personalSystemFunctions}
              required
            />
            {values.system_functions?.includes("other") && (
              <FormField
                name="other_system_function"
                label={t("incident report.other_system_function.label")}
                placeholder={t("incident report.other_system_function.placeholder")}
                required
              />
            )}
            <FormField
              name="affected_systems"
              label={t("incident report.affected_systems.label")}
              type="number"
              required
            />
            <FormField
              name="impact"
              label={t("incident report.impact.label")}
              as="select"
              options={impactLevels}
              required
            />
          </div>
        );
      case "incident":
        return (
          <div className="space-y-6">
            <FormField
              name="time"
              label={t("incident report.time.label")}
              type="datetime-local"
              required
            />
            <FormField
              name="incident_description"
              label={t("incident report.incident_description.label")}
              as="textarea"
              placeholder={t("incident report.incident_description.placeholder")}
            />
            <FormField
              name="incident_files"
              label={t("incident report.upload_files.label")}
              type="file"
              multiple
            />
          </div>
        );
      default:
        return null;
    }
  };

  const getReportTitle = () => {
    switch (type) {
      case 'incident': return t("incident report.report_types.incident");
      case 'vulnerability': return t("incident report.report_types.vulnerability");
      case 'malware': return t("incident report.report_types.malware");
      case 'cop': return t("incident report.report_types.cop");
      case 'abuse': return t("incident report.report_types.abuse");
      default: return t("incident report.report_types.security");
    }
  };

  const getReportIcon = () => {
    switch (type) {
      case 'incident': return faExclamationTriangle;
      case 'vulnerability': return faShieldAlt;
      case 'malware': return faBug;
      default: return faExclamationTriangle;
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setCurrentStep(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {showSuccess && (
        <SuccessNotification
          email={submittedEmail}
          trackingId={submittedTrackingId}
          onClose={handleCloseSuccess}
        />
      )}
      <PageHeader />
      <GradientBanner
        title={getReportTitle()}
        description={t("incident report.ui.description")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.report_incident"), link: "/incident-report" },
          { label: getReportTitle(), link: null },
        ]}
        backgroundIcons={[getReportIcon(), faUser, faBuilding, faClipboardList]}
      />
      <div className="min-h-screen bg-gray-50 py-8 font-nunito-sans">
        <div className="px-dynamic">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Sidebar - Steps */}
            <div className="hidden lg:block lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4 max-h-screen overflow-visible">
                <h2 className="text-xl font-bold text-gray-800 mb-6">{t("incident report.ui.report_progress")}</h2>
                <div className="space-y-4">
                  {steps.map((step, index) => (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`flex items-center p-4 rounded-lg transition-all cursor-pointer ${index === currentStep
                        ? "bg-primary text-white shadow-md"
                        : index < currentStep
                          ? "bg-green-50 text-green-700 border border-green-200"
                          : "bg-gray-50 text-gray-500"
                        }`}
                      onClick={() => setCurrentStep(index)}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${index === currentStep
                        ? "bg-white/20"
                        : index < currentStep
                          ? "bg-green-100"
                          : "bg-gray-200"
                        }`}>
                        <FontAwesomeIcon
                          icon={index < currentStep ? faCheck : step.icon}
                          className={`${index === currentStep
                            ? "text-white"
                            : index < currentStep
                              ? "text-green-600"
                              : "text-gray-400"
                            }`}
                        />
                      </div>
                      <div>
                        <div className="font-medium">{step.title}</div>
                        <div className={`text-sm ${index === currentStep
                          ? "text-white/80"
                          : index < currentStep
                            ? "text-green-600"
                            : "text-gray-400"
                          }`}>
                          {t("incident report.ui.step_of", { current: index + 1, total: steps.length })}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Content - Form */}
            <div className="flex-1">
              <Formik
                initialValues={getInitialValues()}
                validationSchema={formType === "organization" ? organizationValidationSchema : personalValidationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
              >
                {({ values, setFieldValue, validateForm, setTouched }) => (
                  <Form>
                    <ScrollToError />
                    <motion.div
                      key={currentStep}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="bg-white rounded-2xl shadow-lg p-8"
                    >
                      <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">
                          {steps[currentStep].title}
                        </h1>
                        <p className="text-gray-600">
                          {t("incident report.ui.step_of", { current: currentStep + 1, total: steps.length })} - {t("incident report.ui.description")}
                        </p>
                      </div>



                      {renderStepContent(steps[currentStep], values, setFieldValue)}

                      {/* reCAPTCHA - Show only on last step */}
                      {currentStep === steps.length - 1 && (
                        <div className="mt-6">
                          <ReCaptcha
                            ref={(ref) => setRecaptchaRef(ref)}
                            onChange={(token) => setFieldValue('recaptcha', token)}
                            onExpired={() => setFieldValue('recaptcha', '')}
                          />
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8 pt-6 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={handlePrevious}
                          disabled={currentStep === 0}
                          className={`flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-all w-full sm:w-auto ${currentStep === 0
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                            }`}
                        >
                          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                          {t("incident report.ui.previous")}
                        </button>

                        {currentStep === steps.length - 1 ? (
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                          >
                            {isSubmitting ? (
                              <>
                                <InlineSpinner size="sm" className="mr-2 border-white/30 border-t-white" />
                                {t("incident report.ui.submitting")}
                              </>
                            ) : (
                              <>
                                {t("incident report.ui.submit")}
                                <FontAwesomeIcon icon={faCheck} className="ml-2" />
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleNext(validateForm, values, setTouched)}
                            className="flex items-center justify-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all w-full sm:w-auto"
                          >
                            {t("incident report.ui.next")}
                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
      <Toaster position="top-right" />
    </>
  );
};

export default SteppedIncidentForm;
